import { mat4 } from 'gl-matrix';
import { DcmJsDatasetWrapper } from './dcmJsWrapper';
import { DicomRegistration } from './dicomRegistration';
import { DicomRigidRegistration } from './dicomRigidRegistration';
import { DicomDataset } from './engine/dataset';
import { DicomFile } from './engine/reader';
import * as tags from './engine/tags';

/**
 * Builds a {@link DicomRegistration} from the typed dataset model.
 *
 * The equivalent reading dcmjs's keyword-addressed shape reaches the transform
 * as `RegistrationSequence[i].MatrixRegistrationSequence.MatrixSequence`,
 * which works only because that shape forwards a property read on a
 * single-item sequence to the item. Here each step is an explicit index into
 * an array, so the nesting the file actually has is visible in the code.
 */

/** The number of elements in a DICOM frame-of-reference transformation matrix. */
const TRANSFORMATION_MATRIX_LENGTH = 16;

/** A registration the application cannot represent. */
export class UnsupportedRegistrationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'UnsupportedRegistrationError';
    }
}

/**
 * Reads a registration item's transform.
 *
 * DICOM stores the matrix row-major; gl-matrix expects column-major, so the
 * two are transposed on the way in. Getting this backwards would move an image
 * to a plausible but wrong place, which no later check would catch.
 */
function readTransform(item: DicomDataset, sopInstanceUid: string): mat4 {
    const [matrixRegistration] = item.sequence(
        tags.MATRIX_REGISTRATION_SEQUENCE
    );
    const [matrixItem] =
        matrixRegistration?.sequence(tags.MATRIX_SEQUENCE) ?? [];
    const values =
        matrixItem?.numbers(tags.FRAME_OF_REFERENCE_TRANSFORMATION_MATRIX) ??
        [];

    if (values.length === 0) {
        throw new UnsupportedRegistrationError(
            'Rigid registration MatrixSequence missing ' +
                `FrameOfReferenceTransformationMatrix. SOP UID ${sopInstanceUid}`
        );
    }
    if (values.length !== TRANSFORMATION_MATRIX_LENGTH) {
        throw new UnsupportedRegistrationError(
            'Rigid registration FrameOfReferenceTransformationMatrix has a ' +
                `matrix of length ${values.length}. SOP UID ${sopInstanceUid}`
        );
    }

    return mat4.fromValues(
        values[0],
        values[4],
        values[8],
        values[12],
        values[1],
        values[5],
        values[9],
        values[13],
        values[2],
        values[6],
        values[10],
        values[14],
        values[3],
        values[7],
        values[11],
        values[15]
    );
}

/**
 * Reads a rigid spatial registration.
 *
 * A conformant registration carries one item per frame of reference: the fixed
 * frame, identity by construction, and the moving frame with the real
 * transform. The moving one is normally found by taking the first non-identity
 * item — but identity is itself a legal transform, for two frames that
 * coincide, and a registration made entirely of identity items would otherwise
 * fail to load at all. So an item naming a frame other than the fixed one is
 * kept as a fallback.
 *
 * @throws {UnsupportedRegistrationError} for a deformable registration, an
 * empty sequence, or one that names no moving frame.
 */
export function readRegistration(file: DicomFile): DicomRegistration {
    const dataset = file.dataset;
    const sopInstanceUid = dataset.string(tags.SOP_INSTANCE_UID) ?? '';

    if (dataset.has(tags.DEFORMABLE_REGISTRATION_SEQUENCE)) {
        throw new UnsupportedRegistrationError(
            'Attempt to read a deformable registration. Deformable ' +
                'registrations are currently not supported.'
        );
    }

    const items = dataset.sequence(tags.REGISTRATION_SEQUENCE);
    if (!dataset.has(tags.REGISTRATION_SEQUENCE)) {
        throw new UnsupportedRegistrationError(
            'Attempt to decode a registration that does not contain ' +
                'RegistrationSequence.'
        );
    }
    if (items.length === 0) {
        throw new UnsupportedRegistrationError(
            `Unsupported empty RegistrationSequence. SOP UID ${sopInstanceUid}`
        );
    }

    const fixedFrameUid = dataset.string(tags.FRAME_OF_REFERENCE_UID) as string;
    const contentLabel = dataset.string(tags.CONTENT_LABEL) as string;
    let identityMoving:
        | { targetFrameOfReferenceUid: string; transformationMatrix: mat4 }
        | undefined;

    for (const item of items) {
        const transformationMatrix = readTransform(item, sopInstanceUid);
        const targetFrameOfReferenceUid = item.string(
            tags.FRAME_OF_REFERENCE_UID
        ) as string;

        if (!mat4.equals(transformationMatrix, mat4.create())) {
            return new DicomRigidRegistration(
                sopInstanceUid,
                fixedFrameUid,
                targetFrameOfReferenceUid,
                contentLabel,
                transformationMatrix,
                new DcmJsDatasetWrapper(file)
            );
        }
        if (
            identityMoving === undefined &&
            targetFrameOfReferenceUid !== fixedFrameUid
        ) {
            identityMoving = {
                targetFrameOfReferenceUid,
                transformationMatrix,
            };
        }
    }

    if (identityMoving) {
        return new DicomRigidRegistration(
            sopInstanceUid,
            fixedFrameUid,
            identityMoving.targetFrameOfReferenceUid,
            contentLabel,
            identityMoving.transformationMatrix,
            new DcmJsDatasetWrapper(file)
        );
    }

    throw new UnsupportedRegistrationError(
        'Attempt to decode registration, but every item is identity and names ' +
            'the fixed frame of reference — there is no moving frame to register'
    );
}
